import type { Map as MapboxMap } from "mapbox-gl";

import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { useEffect, useRef } from "react";

import {
	applyDrawAppearance,
	removeLingeringDrawStyle,
} from "./draw-appearance";
import {
	createDrawStyles,
	DEFAULT_DRAW_COLOR,
	DEFAULT_DRAW_LINE_WIDTH,
} from "./draw-styles";
import {
	changeDrawMode,
	enterDrawMode,
	getBoundaryFromDraw,
	keepLatestGeometry,
	serializeDrawSyncState,
	syncDrawBoundary,
	type ZoneBoundaryDrawAction,
	type ZoneBoundaryDrawConfig,
} from "./draw-sync";

import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

export type { ZoneBoundaryDrawGeometryType } from "./draw-boundary";
export type {
	ZoneBoundaryDrawAction,
	ZoneBoundaryDrawConfig,
} from "./draw-sync";

/** Attach Mapbox Draw for editing a zone boundary polygon or open perimeter. */
export const useMapboxDraw = (
	map: MapboxMap | null,
	isMapLoaded: boolean,
	config: undefined | ZoneBoundaryDrawConfig,
) => {
	const drawRef = useRef<MapboxDraw | null>(null);
	const configRef = useRef(config);
	const lastSyncedBoundaryRef = useRef<null | string>(null);
	const isApplyingDrawUpdateRef = useRef(false);

	useEffect(() => {
		configRef.current = config;
	}, [config]);

	const hasDrawnFeature = Boolean(config?.boundary);

	useEffect(() => {
		if (!map || !isMapLoaded || !config?.enabled) {
			return;
		}

		const geometryType = config.geometryType ?? "polygon";
		const color = config.color?.trim() || DEFAULT_DRAW_COLOR;
		const lineWidth = config.lineWidth ?? DEFAULT_DRAW_LINE_WIDTH;
		const fillOpacity = config.fillOpacity ?? 0;
		const allowNewDraw = !config.translateOnly && !hasDrawnFeature;
		const draw = new MapboxDraw({
			controls: {
				line_string: allowNewDraw && geometryType === "line",
				polygon: allowNewDraw && geometryType === "polygon",
				trash: !config.translateOnly && hasDrawnFeature,
			},
			defaultMode: "simple_select",
			displayControlsDefault: false,
			styles: createDrawStyles(color, lineWidth, fillOpacity),
		});

		map.addControl(draw, "top-left");
		drawRef.current = draw;
		applyDrawAppearance(map, color, lineWidth, fillOpacity);
		configRef.current?.registerCaptureBoundary?.(() =>
			getBoundaryFromDraw(draw, configRef.current?.geometryType ?? "polygon"),
		);

		const emitBoundary = (action?: ZoneBoundaryDrawAction) => {
			if (isApplyingDrawUpdateRef.current) {
				return;
			}

			const currentGeometryType = configRef.current?.geometryType ?? "polygon";
			const boundary = getBoundaryFromDraw(draw, currentGeometryType);
			lastSyncedBoundaryRef.current = serializeDrawSyncState({
				boundary,
				geometryType: currentGeometryType,
				translateOnly: Boolean(configRef.current?.translateOnly),
			});
			configRef.current?.onChange(boundary, action);
		};

		const resyncFromConfig = () => {
			lastSyncedBoundaryRef.current = null;
			if (configRef.current) {
				syncDrawBoundary(
					draw,
					configRef.current,
					lastSyncedBoundaryRef,
					isApplyingDrawUpdateRef,
				);
			}
		};

		const handleCreate = () => {
			if (configRef.current?.translateOnly) {
				resyncFromConfig();
				return;
			}
			keepLatestGeometry(draw, configRef.current?.geometryType ?? "polygon");
			emitBoundary("create");
		};
		const handleUpdate = (event: { action?: string }) => {
			// Auto/translate-only: whole-outline move stays translate-only. Vertex
			// reshape (`change_coordinates`) unlocks Custom via onChange — do not
			// revert, or the drag is lost before the parent can convert.
			if (
				configRef.current?.translateOnly &&
				event.action !== "move" &&
				event.action !== "change_coordinates"
			) {
				resyncFromConfig();
				return;
			}
			emitBoundary(
				event.action === "move" || event.action === "change_coordinates"
					? event.action
					: "change_coordinates",
			);
		};
		const handleDelete = () => {
			if (configRef.current?.translateOnly) {
				resyncFromConfig();
				return;
			}

			const currentGeometryType = configRef.current?.geometryType ?? "polygon";
			const boundary = getBoundaryFromDraw(draw, currentGeometryType);
			lastSyncedBoundaryRef.current = serializeDrawSyncState({
				boundary,
				geometryType: currentGeometryType,
				translateOnly: Boolean(configRef.current?.translateOnly),
			});
			configRef.current?.onChange(boundary, "delete");

			if (!boundary) {
				isApplyingDrawUpdateRef.current = true;
				try {
					enterDrawMode(draw, currentGeometryType);
				} finally {
					isApplyingDrawUpdateRef.current = false;
				}
			}
		};

		const handleModeChange = (event: { mode?: string }) => {
			if (!configRef.current?.translateOnly) return;
			if (event.mode === "simple_select") return;
			// Allow direct_select so the operator can drag a vertex; the first
			// reshape update converts Auto → Custom in the parent.
			if (event.mode === "direct_select") return;

			isApplyingDrawUpdateRef.current = true;
			try {
				const featureId = configRef.current.featureId;
				changeDrawMode(draw, "simple_select", {
					featureIds: featureId ? [featureId] : [],
				});
			} catch {
				// Stay in the unexpected mode; the next sync retries.
			} finally {
				isApplyingDrawUpdateRef.current = false;
			}
		};

		const applyLiveAppearance = () => {
			const live = configRef.current;
			applyDrawAppearance(
				map,
				live?.color?.trim() || DEFAULT_DRAW_COLOR,
				live?.lineWidth ?? DEFAULT_DRAW_LINE_WIDTH,
				live?.fillOpacity ?? 0,
			);
		};

		map.on("draw.create", handleCreate);
		map.on("draw.update", handleUpdate);
		map.on("draw.delete", handleDelete);
		map.on("draw.modechange", handleModeChange);
		map.on("draw.render", applyLiveAppearance);

		if (configRef.current) {
			syncDrawBoundary(
				draw,
				configRef.current,
				lastSyncedBoundaryRef,
				isApplyingDrawUpdateRef,
			);
		}

		return () => {
			map.off("draw.create", handleCreate);
			map.off("draw.update", handleUpdate);
			map.off("draw.delete", handleDelete);
			map.off("draw.modechange", handleModeChange);
			map.off("draw.render", applyLiveAppearance);
			if (drawRef.current) {
				map.removeControl(drawRef.current);
				drawRef.current = null;
			}
			removeLingeringDrawStyle(map);
			configRef.current?.registerCaptureBoundary?.(null);
			lastSyncedBoundaryRef.current = null;
		};
		// Color and width update via paint properties; remounting Draw would drop in-progress edits.
		// translateOnly is read from configRef so Auto → Custom on vertex drag does
		// not remount mid-gesture. Returning to Auto forces simple_select below.
		// eslint-disable-next-line react-hooks/exhaustive-deps -- appearance and translateOnly are read live via configRef
	}, [
		config?.enabled,
		config?.featureId,
		config?.geometryType,
		hasDrawnFeature,
		isMapLoaded,
		map,
	]);

	useEffect(() => {
		const draw = drawRef.current;
		if (!draw || !config?.enabled || !isMapLoaded || !config.translateOnly) {
			return;
		}

		try {
			const featureId = config.featureId;
			changeDrawMode(draw, "simple_select", {
				featureIds: featureId ? [featureId] : [],
			});
		} catch {
			// Leave Draw in its previous mode; the next sync retries.
		}
	}, [config?.enabled, config?.featureId, config?.translateOnly, isMapLoaded]);

	useEffect(() => {
		if (!map || !isMapLoaded || !config?.enabled) return;
		applyDrawAppearance(
			map,
			config.color?.trim() || DEFAULT_DRAW_COLOR,
			config.lineWidth ?? DEFAULT_DRAW_LINE_WIDTH,
			config.fillOpacity ?? 0,
		);
	}, [
		config?.color,
		config?.enabled,
		config?.fillOpacity,
		config?.lineWidth,
		isMapLoaded,
		map,
	]);

	useEffect(() => {
		const draw = drawRef.current;
		if (!draw || !config?.enabled || !isMapLoaded) {
			return;
		}

		syncDrawBoundary(
			draw,
			config,
			lastSyncedBoundaryRef,
			isApplyingDrawUpdateRef,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- config.onChange identity is stable; boundary is the sync input
	}, [
		config?.boundary,
		config?.enabled,
		config?.featureId,
		config?.geometryType,
		isMapLoaded,
	]);
};
